/**
 * AI 품질관리 Admin API
 * axios_api (Spring Boot) → 외부 AI 품질관리 서버 프록시
 */
import { get as apiGet, post as apiPost, put as apiPut, del as apiDel } from 'utils/axios_api';

const BASE = '/ai-quality/admin';
const TIMEOUT = { timeout: 30000 };
const TIMEOUT_LONG = { timeout: 300000 };   // 평가 실행: QA 건수 × ASIP 응답시간 고려 5분

const get  = (url, config = {}) => apiGet(url, { ...TIMEOUT, ...config });
const post = (url, data, config = {}) => apiPost(url, data, { ...TIMEOUT, ...config });
const put  = (url, data, config = {}) => apiPut(url, data, { ...TIMEOUT, ...config });
const del  = (url, config = {}) => apiDel(url, { ...TIMEOUT, ...config });

// ─────────────────────────────────────────────
// 1. 표준 QA (qa_standard)
// ─────────────────────────────────────────────
export const qaStandardApi = {
    /** GET /api/ai-quality/admin/qa/list */
    getList: (params) => get(`${BASE}/qa/list`, { params }),
    /** GET /api/ai-quality/admin/qa/{id} */
    getOne: (id) => get(`${BASE}/qa/${id}`),
    /** POST /api/ai-quality/admin/qa */
    create: (data) => post(`${BASE}/qa`, data),
    /** PUT /api/ai-quality/admin/qa/{id} */
    update: (id, data) => put(`${BASE}/qa/${id}`, data),
    /** DELETE /api/ai-quality/admin/qa/{id} */
    remove: (id) => del(`${BASE}/qa/${id}`),
};

// ─────────────────────────────────────────────
// 2. 평가 (qa_evaluation_batch / qa_evaluation_result)
// ─────────────────────────────────────────────
export const evaluationApi = {
    /** POST /api/ai-quality/admin/evaluation/run — 평가 실행 (timeout 60초) */
    run: (data) => post(`${BASE}/evaluation/run`, data, TIMEOUT_LONG),
    /** GET /api/ai-quality/admin/evaluation/batches */
    getBatches: (params) => get(`${BASE}/evaluation/batches`, { params }),
    /** GET /api/ai-quality/admin/evaluation/results */
    getResults: (params) => get(`${BASE}/evaluation/results`, { params }),
    /** GET /api/ai-quality/admin/evaluation/results/{id} */
    getResult: (id) => get(`${BASE}/evaluation/results/${id}`),
    /** GET /api/ai-quality/admin/evaluation/results/{id}/detail */
    getResultDetail: (id) => get(`${BASE}/evaluation/results/${id}/detail`),
};

// ─────────────────────────────────────────────
// 3. 검토 (qa_review)
// ─────────────────────────────────────────────
export const reviewApi = {
    /** GET /api/ai-quality/admin/review/list */
    getList: (params) => get(`${BASE}/review/list`, { params }),
    /** GET /api/ai-quality/admin/review/{id} */
    getOne: (id) => get(`${BASE}/review/${id}`),
    /** POST /api/ai-quality/admin/review */
    create: (data) => post(`${BASE}/review`, data),
    /** PUT /api/ai-quality/admin/review/{id} */
    update: (id, data) => put(`${BASE}/review/${id}`, data),
    /** POST /api/ai-quality/admin/review/{id}/complete */
    complete: (id, data) => post(`${BASE}/review/${id}/complete`, data),
};

// ─────────────────────────────────────────────
// 4. 정책 (qa_policy_rule / qa_policy_apply_history)
// ─────────────────────────────────────────────
export const policyApi = {
    /** GET /api/ai-quality/admin/policy/list */
    getList: (params) => get(`${BASE}/policy/list`, { params }),
    /** GET /api/ai-quality/admin/policy/{id} */
    getOne: (id) => get(`${BASE}/policy/${id}`),
    /** POST /api/ai-quality/admin/policy */
    create: (data) => post(`${BASE}/policy`, data),
    /** PUT /api/ai-quality/admin/policy/{id} */
    update: (id, data) => put(`${BASE}/policy/${id}`, data),
    /** POST /api/ai-quality/admin/policy/{id}/activate */
    activate: (id) => post(`${BASE}/policy/${id}/activate`),
    /** POST /api/ai-quality/admin/policy/{id}/deactivate */
    deactivate: (id) => post(`${BASE}/policy/${id}/deactivate`),
    /** DELETE /api/ai-quality/admin/policy/{id} */
    remove: (id) => del(`${BASE}/policy/${id}`),
    /** POST /api/ai-quality/admin/policy/apply */
    apply: (data) => post(`${BASE}/policy/apply`, data),
    /** GET /api/ai-quality/admin/policy/apply-history */
    getApplyHistory: (params) => get(`${BASE}/policy/apply-history`, { params }),
};

// ─────────────────────────────────────────────
// 5. 이력 (qa_regeneration_history)
// ─────────────────────────────────────────────
export const historyApi = {
    /** GET /api/ai-quality/admin/history/list */
    getList: (params) => get(`${BASE}/history/list`, { params }),
    /** POST /api/ai-quality/admin/history/regenerate */
    regenerate: (data) => post(`${BASE}/history/regenerate`, data),
    /** POST /api/ai-quality/admin/history/approve */
    approve: (data) => post(`${BASE}/history/approve`, data),
    /** POST /api/ai-quality/admin/history/reject */
    reject: (data) => post(`${BASE}/history/reject`, data),
};

// ─────────────────────────────────────────────
// 6. 시스템 설정 (qa_system_setting)
// ─────────────────────────────────────────────
export const settingsApi = {
    /** GET /api/ai-quality/admin/settings */
    getAll: (params) => get(`${BASE}/settings`, { params }),
    /** PUT /api/ai-quality/admin/settings */
    update: (data) => put(`${BASE}/settings`, data),
    /** GET /api/ai-quality/admin/settings/schema */
    getSchema: () => get(`${BASE}/settings/schema`),
    /** POST /api/ai-quality/admin/settings/reload */
    reload: (data) => post(`${BASE}/settings/reload`, data),
};

// ─────────────────────────────────────────────
// 7. 시스템 상태 (health)
// ─────────────────────────────────────────────
export const healthApi = {
    /** GET /api/ai-quality/admin/health */
    check: () => get(`${BASE}/health`),
};
